import React from 'react';
import { DateRangePicker } from 'react-date-range';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import { amountFormat } from '../../../Component/helper';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import { DateRange } from '@mui/icons-material';
import BigButton from '../../../Component/BigButton';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputTextField from '../../../Component/Reusables/InputTextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import LimitService from '../../../Services/LimitService';
import AlertDialogPopop from '../../../Component/AlertDialogPopop/AlertDialogPopop';
import { FormGroup, Box, Typography } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CustomDialog from '../../NavBar/GeneralSettingDialog/CustomDialog';
import moment from "moment";
import { formatLimit, convertToNumber } from '../../../Component/config';
import { ShowAlert } from '../../../Component/ShowAlert';
import { UserSettingsState, UserSettingsProps, UserCategories, TableData,  } from '../Interface';
import { SyncContext, SyncContextType } from '../../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../../Services/UserAndPermissionService';
import { loadFromLocalStorage } from '../../../Common/Validation/LocalStorage';

// Interface for ActionButtons
export enum ActionButtons {
    Save = 'save',
    Cancel = 'cancel'
}

// Main class UserSettings 
class UserSettings extends React.Component<UserSettingsProps, UserSettingsState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    constructor(props: UserSettingsProps) {
        super(props);
        // Initial state
        this.state = {
            selectedDateRange:
            {
                startDate: new Date(),
                endDate: new Date()
            },
            selectedTempDateRange:
            {
                startDate: new Date(),
                endDate: new Date()
            },
            popOverTargetElementForDateRange: null,
            popOverTargetElementForTempDateRange: null,
            noOfTransactionsPerDay: '0',
            noOfTransactionsPerMonth: '0',
            weeklyLimit: '0.00',
            hourlyLimit: '0.00',
            dailyLimit: '0.00',
            monthlyLimit: '0.00',
            yearlyLimit: '0.00',
            temporaryLimit: '0.00',
            maxLimitPerTransaction: '0.00',
            inputType: 'text',
            userType: UserCategories.StandardUser,
            isSingleTransaction: false,
            isLimitConfirm: false,
            existingLimits: [],
            openViewDetails: false,
            standardHourlyLimit: '0.00',
            standardDailyLimit: '0.00',
            standardWeeklyLimit: '0.00',
            standardMonthlyLimit: '0.00',
            standardYearlyLimit: '0.00',
            premiumHourlyLimit: '0.00',
            premiumDailyLimit: '0.00',
            premiumWeeklyLimit: '0.00',
            premiumMonthlyLimit: '0.00',
            premiumYearlyLimit: '0.00',
            businessHourlyLimit: '0.00',
            businessDailyLimit: '0.00',
            businessWeeklyLimit: '0.00',
            businessMonthlyLimit: '0.00',
            businessYearlyLimit: '0.00',
            initialUserType: UserCategories.StandardUser,
            hourlyLimitError: "",
            dailyLimitError: "",
            weeklyLimitError: "",
            monthlyLimitError: "",
            isDeleteHistory: false,
            selectedLimit: '',
            annualLimitError: "",
            maxLimitPerTransactionError: "",
            checkedFields: {
                hourlyLimit: false,
                dailyLimit: false,
                weeklyLimit: false,
                monthlyLimit: false,
                yearlyLimit: false,
                maxLimitPerTransaction: false
            },
            currencySymbol: '$',
            accessToken:"",
            isUserSettingSaveBtnVisible: true,
            optionalSingleTransactionLimit: "0.00",
            initialOptionalSingleTransactionLimit: "0.00",
        };
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        const isUserSettingSaveBtnVisible: boolean = permissionResponse?.permissions?.userDetails?.isUserSettingSaveBtnVisible;


                        this.setState({ isUserSettingSaveBtnVisible }, () => {
                            this.getUserSettings();
                        });

                    } else {
                        this.getUserSettings();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
     }

    // To Update parent component when state changes
    componentDidUpdate(prevProps: Readonly<UserSettingsProps>, prevState: Readonly<UserSettingsState>) {
        if (prevState !== this.state) {
            this.notifyValidationStatus();
        }
    }

    // To pass the validation status to the parent component
    notifyValidationStatus = () => {
        if (this.props.onValidationChange) {
            this.props.onValidationChange(this.hasErrors());
        }
    };

    // GetUserSettings function to populate the user settings
    private async getUserSettings(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            LimitService.getUserSettings(this.props.accessToken, this.props.userId)
                .then(tr => {
                    if (tr.ok) {
                        tr.json().then(resp => {
                            this.setState({
                                userType: resp.category || UserCategories.StandardUser,
                                initialUserType: resp.category || UserCategories.StandardUser,
                                existingLimits: resp.existingLimits || [],
                                optionalSingleTransactionLimit: resp.transactionLimit || "0.00",
                                initialOptionalSingleTransactionLimit: resp.transactionLimit || "0.00",
                                // Standard Limits
                                standardHourlyLimit: resp.globalLimits?.limitsByCategories?.standard?.hourlyLimit || '0.00',
                                standardDailyLimit: resp.globalLimits?.limitsByCategories?.standard?.dailyLimit || '0.00',
                                standardWeeklyLimit: resp.globalLimits?.limitsByCategories?.standard?.weeklyLimit || '0.00',
                                standardMonthlyLimit: resp.globalLimits?.limitsByCategories?.standard?.monthlyLimit || '0.00',
                                standardYearlyLimit: resp.globalLimits?.limitsByCategories?.standard?.yearlyLimit || '0.00',
                                // Premium Limits
                                premiumHourlyLimit: resp.globalLimits?.limitsByCategories?.premium?.hourlyLimit || '0.00',
                                premiumDailyLimit: resp.globalLimits?.limitsByCategories?.premium?.dailyLimit || '0.00',
                                premiumWeeklyLimit: resp.globalLimits?.limitsByCategories?.premium?.weeklyLimit || '0.00',
                                premiumMonthlyLimit: resp.globalLimits?.limitsByCategories?.premium?.monthlyLimit || '0.00',
                                premiumYearlyLimit: resp.globalLimits?.limitsByCategories?.premium?.yearlyLimit || '0.00',
                                // Business Limits
                                businessHourlyLimit: resp.globalLimits?.limitsByCategories?.business?.hourlyLimit || '0.00',
                                businessDailyLimit: resp.globalLimits?.limitsByCategories?.business?.dailyLimit || '0.00',
                                businessWeeklyLimit: resp.globalLimits?.limitsByCategories?.business?.weeklyLimit || '0.00',
                                businessMonthlyLimit: resp.globalLimits?.limitsByCategories?.business?.monthlyLimit || '0.00',
                                businessYearlyLimit: resp.globalLimits?.limitsByCategories?.business?.yearlyLimit || '0.00',
                            }, () => {
                                resolve();
                            });
                        });
                    } else {
                        reject("Failed to fetch user settings");
                    }
                })
                .catch(err => reject(err));
        });
    }

    // HandleSelectDateRange function to handle the date range
    private handleSelectDateRange = (ranges: any, updateValue: keyof UserSettingsState) => {
        const selectedRange = ranges.range1;
        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: selectedRange,
        }));
    };

    // HandleOpenPopover function to handle the calander popover
    private handleOpenPopover = (
        event: React.MouseEvent<HTMLElement>,
        popoverType: 'dateRange' | 'tempDateRange'
    ) => {
        if (popoverType === 'dateRange') {
            this.setState({ popOverTargetElementForDateRange: event.currentTarget });
        } else {
            this.setState({ popOverTargetElementForTempDateRange: event.currentTarget });
        }
    };

    // Handle apply date range
    private applyDateRange = (updateValue: keyof UserSettingsState, popoverType: 'dateRange' | 'tempDateRange') => {
        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: this.state[updateValue],
            [popoverType === 'dateRange'
                ? 'popOverTargetElementForDateRange'
                : 'popOverTargetElementForTempDateRange']: null,
        }));
    };

    // HandleSelectChange function to handle the select user type
    private handleSelectChange = (event: SelectChangeEvent<UserCategories>) => {
        this.setState({ userType: event.target.value as UserCategories });
    };

    // HandleLimitChange function to handle the limit changes
    handleLimitChange = (field: keyof UserSettingsState, value: string) => {
        const { hourlyLimit, dailyLimit, weeklyLimit, monthlyLimit, yearlyLimit, maxLimitPerTransaction } = this.state;
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999; //  1 billion max limit

        // Allow only one decimal point
        const decimalCount = (sanitizedValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
            // If there are multiple decimal points, remove everything after the first one
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        // Restrict max limit
        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        let newValueNumber = convertToNumber(sanitizedValue) || 0;
        let hourlyLimitNumber = convertToNumber(hourlyLimit) || 0;
        let dailyLimitNumber = convertToNumber(dailyLimit) || 0;
        let weeklyLimitNumber = convertToNumber(weeklyLimit) || 0;
        let monthlyLimitNumber = convertToNumber(monthlyLimit) || 0;
        let annualLimitNumber = convertToNumber(yearlyLimit) || 0;
        let maxLimitPerTransNumber = convertToNumber(maxLimitPerTransaction) || 0;

        const errors = this.validateLimits(field, newValueNumber, { hourlyLimitNumber, dailyLimitNumber, weeklyLimitNumber, monthlyLimitNumber, annualLimitNumber, maxLimitPerTransNumber });
        this.setState((prevState) => ({
            ...prevState,
            [field]: sanitizedValue,
            ...errors,
        }));
    };

    // ValidateLimits function to validate the limits and return the errors
    validateLimits = (
        field: keyof UserSettingsState,
        newValue: number,
        limits: { hourlyLimitNumber: number; dailyLimitNumber: number; weeklyLimitNumber: number; monthlyLimitNumber: number, annualLimitNumber: number, maxLimitPerTransNumber: number }
    ): Partial<UserSettingsState> => {
        const { hourlyLimitNumber, dailyLimitNumber, weeklyLimitNumber, monthlyLimitNumber, annualLimitNumber, maxLimitPerTransNumber } = limits;
        let errors: Partial<UserSettingsState> = {};

        switch (field) {
            // case "hourlyLimit":
            //     if (dailyLimitNumber > 0 && newValue > dailyLimitNumber) {
            //         errors.hourlyLimitError = "Hourly cannot exceed daily.";
            //     } else if (weeklyLimitNumber > 0 && newValue > weeklyLimitNumber) {
            //         errors.hourlyLimitError = "Hourly cannot exceed weekly.";
            //     } else if (monthlyLimitNumber > 0 && newValue > monthlyLimitNumber) {
            //         errors.hourlyLimitError = "Hourly cannot exceed monthly.";
            //     } else if (annualLimitNumber > 0 && newValue > annualLimitNumber) {
            //         errors.hourlyLimitError = "Hourly cannot exceed annual.";
            //     } else {
            //         errors.hourlyLimitError = "";
            //     }
            //     break;
            case "dailyLimit":
                if (hourlyLimitNumber > 0 && newValue < hourlyLimitNumber) {
                    errors.dailyLimitError = "Daily cannot be less than hourly.";
                } else if (weeklyLimitNumber > 0 && newValue > weeklyLimitNumber) {
                    errors.dailyLimitError = "Daily cannot exceed weekly.";
                } else if (monthlyLimitNumber > 0 && newValue > monthlyLimitNumber) {
                    errors.dailyLimitError = "Daily cannot exceed monthly.";
                } else if (annualLimitNumber > 0 && newValue > annualLimitNumber) {
                    errors.dailyLimitError = "Daily cannot exceed annual.";
                } else {
                    errors.dailyLimitError = "";
                }
                break;
            case "weeklyLimit":
                if (dailyLimitNumber > 0 && newValue < dailyLimitNumber) {
                    errors.weeklyLimitError = "Weekly cannot be less than daily.";
                } else if (hourlyLimitNumber > 0 && newValue < hourlyLimitNumber) {
                    errors.weeklyLimitError = "Weekly cannot be less than hourly.";
                } else if (monthlyLimitNumber > 0 && newValue > monthlyLimitNumber) {
                    errors.weeklyLimitError = "Weekly cannot exceed monthly.";
                } else if (annualLimitNumber > 0 && newValue > annualLimitNumber) {
                    errors.weeklyLimitError = "Weekly cannot exceed annual.";
                } else {
                    errors.weeklyLimitError = "";
                }
                break;
            case "monthlyLimit":
                if (weeklyLimitNumber > 0 && newValue < weeklyLimitNumber) {
                    errors.monthlyLimitError = "Monthly cannot be less than weekly.";
                } else if (dailyLimitNumber > 0 && newValue < dailyLimitNumber) {
                    errors.monthlyLimitError = "Monthly cannot be less than daily.";
                } else if (hourlyLimitNumber > 0 && newValue < hourlyLimitNumber) {
                    errors.monthlyLimitError = "Monthly cannot be less than hourly.";
                } else if (annualLimitNumber > 0 && newValue > annualLimitNumber) {
                    errors.monthlyLimitError = "Monthly cannot exceed annual.";
                } else {
                    errors.monthlyLimitError = "";
                }
                break;
            case "yearlyLimit":
                if (monthlyLimitNumber > 0 && newValue < monthlyLimitNumber) {
                    errors.annualLimitError = "Yearly cannot be less than monthly.";
                } else if (weeklyLimitNumber > 0 && newValue < weeklyLimitNumber) {
                    errors.annualLimitError = "Yearly cannot be less than weekly.";
                } else if (dailyLimitNumber > 0 && newValue < dailyLimitNumber) {
                    errors.annualLimitError = "Yearly cannot be less than daily.";
                } else if (hourlyLimitNumber > 0 && newValue < hourlyLimitNumber) {
                    errors.annualLimitError = "Yearly cannot be less than hourly.";
                } else {
                    errors.annualLimitError = "";
                }
                break;
            default:
                break;
        }


        return errors;
    };

    // To check the limit conflicts
    hasErrors = (): boolean => {
        const { dailyLimit, weeklyLimit, monthlyLimit, yearlyLimit, maxLimitPerTransaction,
            dailyLimitError, weeklyLimitError, monthlyLimitError, annualLimitError,
            checkedFields, isUserSettingSaveBtnVisible } = this.state;

        return !!(!isUserSettingSaveBtnVisible ||
            (checkedFields.dailyLimit && (dailyLimitError || dailyLimit === "0.00")) ||
            (checkedFields.weeklyLimit && (weeklyLimitError || weeklyLimit === "0.00")) ||
            (checkedFields.monthlyLimit && (monthlyLimitError || monthlyLimit === "0.00")) ||
            (checkedFields.yearlyLimit && (annualLimitError || yearlyLimit === "0.00")) ||
            (checkedFields.maxLimitPerTransaction && (maxLimitPerTransaction === "0.00"))
        );
    };

    // Handle limitvalue when focus is lost
    handleLimitBlur = (field: keyof UserSettingsState) => {
        const value = this.state[field];
        const n_ = typeof value === 'string' && value ? value : "0";

        const updatedAmount = amountFormat(
            parseFloat(n_.replace(/,/g, "")),
            "en-US",
            "USD"
        ).replace("$", "");

        this.setState((prevState) => ({
            ...prevState,
            [field]: updatedAmount,
            [`${field}InputType`]: "text",
        }));
    };

    //Handle limitvalue when focus is gained
    handleLimitFocus = (field: keyof UserSettingsState) => {
        const value = this.state[field];
        if (typeof value === 'string') {
            const n_ = value.replace(/,/g, "");
            this.setState((prevState) => ({
                ...prevState,
                [field]: n_,
                [`${field}InputType`]: "number"
            }));
        }
    };

    // HandleClose function to handle on save and cancel
    private async handleClose(action: string) {
        if (action === ActionButtons.Save) {
            const convertToNumber = (value: string) => parseFloat(value.replace(/,/g, ''));
            const { hourlyLimit, dailyLimit, weeklyLimit, monthlyLimit, yearlyLimit, selectedDateRange, maxLimitPerTransaction, isSingleTransaction, optionalSingleTransactionLimit, initialOptionalSingleTransactionLimit } = this.state;
            const startDate = moment(selectedDateRange.startDate).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            const endDate = moment(selectedDateRange.endDate).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');

            let postData: { [key: string]: any } = {
                userId: this.props.userId,
                userCategory: this.state.userType,
                transactionLimit: convertToNumber(optionalSingleTransactionLimit), // single transaction limit applies to all categories
            };

            const limits = { hourlyLimit, dailyLimit, weeklyLimit, monthlyLimit, yearlyLimit };

            // // Default all is<LimitKey>Enabled fields to false
            // Object.keys(limits).forEach((key) => {
            //     postData[`is${key.charAt(0).toUpperCase() + key.slice(1)}Enabled`] = false;
            // });

            Object.entries(limits).forEach(([key, value]) => {
                if (value !== "0.00") { // Only send non-zero positive limits parameters
                    postData[key] = convertToNumber(value);
                    postData[`is${key.charAt(0).toUpperCase() + key.slice(1)}Enabled`] = true; // Add is<LimitKey>Enabled field to indicate that the limit is enabled

                }
            });

            if (Object.keys(postData).length > 3) {
                Object.assign(postData, {
                    startDate: startDate,
                    endDate: endDate,
                    isSingleTransaction: isSingleTransaction,
                });
            }
            try {
                if (this.state.initialUserType !== this.state.userType || initialOptionalSingleTransactionLimit !== optionalSingleTransactionLimit || Object.keys(postData).length > 3) {
                    const rsp = await LimitService.updateUserSettings(this.props.accessToken, postData);
                    if (rsp.status === 200) {
                        // if (this.props.onClose) {
                        //     this.props.onClose(false, action);
                        // }
                        await this.getUserSettings();
                        ShowAlert("Success", "Changes saved successfully!", "success");
                    } else {
                        console.log("Error updating user settings");
                        throw new Error();
                    }
                }
                else {
                    //this.props.onClose(false, action);
                    console.log("No new inputs have been entered. Restricted API Call");
                    ShowAlert("PassportPay", "No Changes to Save", 'error')
                }
            } catch (error) {
                console.error("Error updating user settings:", error);
                ShowAlert("Alert", "Failed to Save Chanages . Please try again later.", "error");
            }
        } else if (this.props.onClose) {
            if (this.state.isDeleteHistory) {
                this.setState({
                    isDeleteHistory: false
                });
            } else {
                this.props.onClose(false, action);
            }
        }

    }

    // HandleDelete function to handle the existing limits
    handleDelete = async (id: string) => {
        const selectedLimit = this.state.existingLimits.find(limit => limit.transactionLimitId === id);
        try {
            if (selectedLimit && selectedLimit.transactionLimitId) {
                const response = await LimitService.deleteSelectedExistingLimits(this.props.accessToken, selectedLimit.transactionLimitId);

                if (!response.ok) {
                    console.error("Error deleting selected limit:", response.statusText);
                    throw new Error();
                } else {
                    if (this.state.isDeleteHistory) {
                        this.setState({
                            isDeleteHistory: false
                        })
                    }
                    await this.getUserSettings()
                    ShowAlert("Success", "Limit deleted successfully!", "success");
                }
                // this.setState(prevState => ({
                //     existingLimits: prevState.existingLimits.filter(limit => limit.transactionLimitId !== id)
                // }));
            } else {
                console.log("No matching limit found for deletion.");
                throw new Error();
            }
        } catch (error) {
            console.error("Error deleting selected limit:", error);
            ShowAlert("Alert", "Failed to delete limit. Please try again later.", "error");
        }
    };

    // HandleCheckboxChange function to handle the limit checkbox
    handleCheckboxChange = (field: string, checked: boolean) => {
        this.setState((prevState) => {
            let updatedState: any = {
                checkedFields: {
                    ...prevState.checkedFields,
                    [field]: checked,
                }
            };

            if (!checked) {
                updatedState[field] = "0.00";
                updatedState[`${field}Error`] = "";
            }

            return updatedState;
        }, this.hasErrors);
    };

    // GenerateTableData function to generate the table data for view details dialog
    generateTableData = (): TableData => {
        const {
            currencySymbol,
            //standardHourlyLimit,
            standardDailyLimit,
            standardWeeklyLimit,
            standardMonthlyLimit,
            standardYearlyLimit,
            //premiumHourlyLimit,
            premiumDailyLimit,
            premiumWeeklyLimit,
            premiumMonthlyLimit,
            premiumYearlyLimit,
            //businessHourlyLimit,
            businessDailyLimit,
            businessWeeklyLimit,
            businessMonthlyLimit,
            businessYearlyLimit
        } = this.state;
        const formatLimits = (limit: string | number) => formatLimit(limit, "en-US", "USD", currencySymbol);

        return {
            rowHeaders: ['Daily', 'Weekly', 'Monthly', 'Yearly'],
            standard: [standardDailyLimit, standardWeeklyLimit, standardMonthlyLimit, standardYearlyLimit].map(formatLimits),
            premium: [premiumDailyLimit, premiumWeeklyLimit, premiumMonthlyLimit, premiumYearlyLimit].map(formatLimits),
            business: [businessDailyLimit, businessWeeklyLimit, businessMonthlyLimit, businessYearlyLimit].map(formatLimits)
        };
    };

    public render() {
        const { selectedDateRange,
            popOverTargetElementForDateRange,
            dailyLimit,
            weeklyLimit,
            monthlyLimit,
            yearlyLimit,
            isSingleTransaction,
            dailyLimitError,
            weeklyLimitError,
            monthlyLimitError,
            annualLimitError,
            checkedFields,
            currencySymbol,
            optionalSingleTransactionLimit } = this.state;
        return (
            <>
                <FormGroup>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <fieldset className="fieldset-border">
                            <FormLabel>Common settings</FormLabel>
                            <Box style={{ display: 'flex', gap: '20px' }}>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <FormLabel className='input-field-label'>Set user tier</FormLabel>
                                            <Box>
                                                <span
                                                    style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                                                    onClick={() => this.setState({ openViewDetails: true })}
                                                >
                                                    View Details
                                                </span>
                                                <CustomDialog
                                                    open={this.state.openViewDetails}
                                                    onClose={() => this.setState({ openViewDetails: false })}
                                                    title="Tier Thresholds"
                                                    tableData={this.generateTableData()}
                                                />
                                            </Box>
                                        </Box>
                                        <Select
                                            value={this.state.userType}
                                            onChange={this.handleSelectChange}
                                            color='secondary'
                                            variant="outlined"
                                            fullWidth
                                        >
                                            {Object.values(UserCategories).map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormGroup>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <InputTextField
                                        label="Single transaction limit"
                                        value={optionalSingleTransactionLimit}
                                        startAdornment={currencySymbol}
                                        inputType="number"
                                        onChange={(value) => this.handleLimitChange('optionalSingleTransactionLimit', value)}
                                        onBlur={() => this.handleLimitBlur('optionalSingleTransactionLimit')}
                                        onFocus={() => this.handleLimitFocus('optionalSingleTransactionLimit')}
                                    />
                                </Box>
                                <Box style={{ flex: 1 }}></Box>
                                <Box style={{ flex: 1 }}></Box>
                            </Box>
                        </fieldset>
                        <fieldset className="fieldset-border">
                            <FormLabel>Set new custom limits</FormLabel>
                            <Box style={{ display: 'flex', gap: '20px' }}>
                                {/* <Box style={{ flex: 1 }}>
                                            <InputTextField
                                                label="Hourly"
                                                value={hourlyLimit}
                                                startAdornment={currencySymbol}
                                                inputType={'text'}
                                                onChange={(value) => this.handleLimitChange('hourlyLimit', value)}
                                                onBlur={() => this.handleLimitBlur('hourlyLimit')}
                                                onFocus={() => this.handleLimitFocus('hourlyLimit')}
                                                error={!!hourlyLimitError}
                                                helperText={hourlyLimitError}
                                                checkedValue={checkedFields.hourlyLimit}
                                                onCheckboxChange={(checked) => this.handleCheckboxChange("hourlyLimit", checked)}
                                                disabled={!checkedFields.hourlyLimit}
                                            />
                                        </Box> */}
                                <Box style={{ flex: 1 }}>
                                    <InputTextField
                                        label="Daily"
                                        value={dailyLimit}
                                        startAdornment={currencySymbol}
                                        inputType="number"
                                        onChange={(value) => this.handleLimitChange('dailyLimit', value)}
                                        onBlur={() => this.handleLimitBlur('dailyLimit')}
                                        onFocus={() => this.handleLimitFocus('dailyLimit')}
                                        error={!!dailyLimitError}
                                        helperText={dailyLimitError}
                                        checkedValue={checkedFields.dailyLimit}
                                        onCheckboxChange={(checked) => this.handleCheckboxChange("dailyLimit", checked)}
                                        disabled={!checkedFields.dailyLimit}
                                    />
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <InputTextField
                                        label="Weekly"
                                        value={weeklyLimit}
                                        startAdornment={currencySymbol}
                                        inputType="number"
                                        onChange={(value) => this.handleLimitChange('weeklyLimit', value)}
                                        onBlur={() => this.handleLimitBlur('weeklyLimit')}
                                        onFocus={() => this.handleLimitFocus('weeklyLimit')}
                                        error={!!weeklyLimitError}
                                        helperText={weeklyLimitError}
                                        checkedValue={checkedFields.weeklyLimit}
                                        onCheckboxChange={(checked) => this.handleCheckboxChange("weeklyLimit", checked)}
                                        disabled={!checkedFields.weeklyLimit}
                                    />
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <InputTextField
                                        label="Monthly"
                                        value={monthlyLimit}
                                        startAdornment={currencySymbol}
                                        inputType="number"
                                        onChange={(value) => this.handleLimitChange('monthlyLimit', value)}
                                        onBlur={() => this.handleLimitBlur('monthlyLimit')}
                                        onFocus={() => this.handleLimitFocus('monthlyLimit')}
                                        error={!!monthlyLimitError}
                                        helperText={monthlyLimitError}
                                        checkedValue={checkedFields.monthlyLimit}
                                        onCheckboxChange={(checked) => this.handleCheckboxChange("monthlyLimit", checked)}
                                        disabled={!checkedFields.monthlyLimit}
                                    />
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <InputTextField
                                        label="Yearly"
                                        value={yearlyLimit}
                                        startAdornment={currencySymbol}
                                        inputType="number"
                                        onChange={(value) => this.handleLimitChange('yearlyLimit', value)}
                                        onBlur={() => this.handleLimitBlur('yearlyLimit')}
                                        onFocus={() => this.handleLimitFocus('yearlyLimit')}
                                        error={!!annualLimitError}
                                        helperText={annualLimitError}
                                        checkedValue={checkedFields.yearlyLimit}
                                        onCheckboxChange={(checked) => this.handleCheckboxChange("yearlyLimit", checked)}
                                        disabled={!checkedFields.yearlyLimit}
                                    />
                                </Box>
                            </Box>
                            <Box style={{ display: 'flex', gap: '20px', alignItems: "end", marginTop: "6px" }}>
                                <Box style={{ flex: 1 }}>
                                    <FormGroup>
                                        <FormLabel className='input-field-label'>Expiration date</FormLabel>
                                        <TextField
                                            variant="outlined"
                                            sx={{ minWidth: "250px" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={(event) =>
                                                            this.handleOpenPopover(event, 'dateRange')
                                                        }
                                                    >
                                                        <DateRange />
                                                    </IconButton>
                                                ),
                                            }}
                                            value={`${selectedDateRange.startDate.toLocaleDateString()} - ${selectedDateRange.endDate.toLocaleDateString()}`}
                                        />
                                        <Popover
                                            id={'date-range-popover'}
                                            open={Boolean(popOverTargetElementForDateRange)}
                                            anchorEl={popOverTargetElementForDateRange}
                                            onClose={() =>
                                                this.setState({ popOverTargetElementForDateRange: null })
                                            }
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right',
                                            }}
                                        >
                                            <Box className="popover-calendar">
                                                <DateRangePicker
                                                    ranges={[selectedDateRange]}
                                                    onChange={(ranges: any) => {
                                                        this.handleSelectDateRange(ranges, 'selectedDateRange');
                                                    }}
                                                    minDate={new Date()}
                                                />
                                                <BigButton
                                                    disabled={false}
                                                    width="80%"
                                                    onClick={() => {
                                                        this.applyDateRange('selectedDateRange', 'dateRange');
                                                    }}
                                                    buttonText="Apply"
                                                />
                                            </Box>
                                        </Popover>
                                    </FormGroup>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={isSingleTransaction} onChange={() => {
                                                this.setState({
                                                    isSingleTransaction: !isSingleTransaction
                                                })
                                            }} color='secondary' name="isSingleTransaction" />
                                        }
                                        label="Single transaction only"
                                    />
                                </Box>
                                <Box style={{ flex: 1 }}> </Box>
                                <Box style={{ flex: 1 }}></Box>
                            </Box>
                        </fieldset>
                        <fieldset className="fieldset-border">
                            <FormLabel>Existing custom limits</FormLabel>
                            <TableContainer>
                                <Table size="medium">
                                    <TableHead>
                                        <TableRow>
                                            {/* <TableCell><Typography variant="body1">Hourly</Typography></TableCell> */}
                                            <TableCell><Typography variant="body1">Daily</Typography></TableCell>
                                            <TableCell><Typography variant="body1">Weekly</Typography></TableCell>
                                            <TableCell><Typography variant="body1">Monthly</Typography></TableCell>
                                            <TableCell><Typography variant="body1">Yearly</Typography></TableCell>
                                            <TableCell><Typography variant="body1">Single Trans</Typography></TableCell>
                                            <TableCell><Typography variant="body1">Start Date</Typography></TableCell>
                                            <TableCell><Typography variant="body1">End Date</Typography></TableCell>
                                            <TableCell><Typography variant="body1">Actions</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {this.state.existingLimits.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        No existing limits available.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            this.state.existingLimits.map((limit) => (
                                                <TableRow key={limit.transactionLimitId}>
                                                    {/* <TableCell>{formatLimit(limit.hourlyLimit, "en-US", "USD", "$")}</TableCell> */}
                                                    <TableCell>{formatLimit(limit.dailyLimit, "en-US", "USD", "$")}</TableCell>
                                                    <TableCell>{formatLimit(limit.weeklyLimit, "en-US", "USD", "$")}</TableCell>
                                                    <TableCell>{formatLimit(limit.monthlyLimit, "en-US", "USD", "$")}</TableCell>
                                                    <TableCell>{formatLimit(limit.yearlyLimit, "en-US", "USD", "$")}</TableCell>
                                                    <TableCell>{limit.isSingleTransaction ? "Yes" : "NO"}</TableCell>
                                                    <TableCell>{new Date(limit.startDate).toLocaleDateString()}</TableCell>
                                                    <TableCell>{new Date(limit.endDate).toLocaleDateString()}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() => this.setState({
                                                                isDeleteHistory: true,
                                                                selectedLimit: limit.transactionLimitId
                                                            })}
                                                            size="small"
                                                        >
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </fieldset>
                    </Box>
                </FormGroup>
                <AlertDialogPopop
                    open={this.state.isLimitConfirm}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to save the settings?`}
                    onClose={(state_, action_) => {
                        this.setState({
                            isLimitConfirm: false
                        }, () => {
                            if (action_ == "ok") {
                                this.handleClose(ActionButtons.Save);
                            }
                        })
                    }}
                />
                <AlertDialogPopop
                    open={this.state.isDeleteHistory}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to delete the selected existing limits?`}
                    onClose={(state_, action_) => {
                        this.setState(() => {
                            if (action_ == "ok") {
                                this.handleDelete(this.state.selectedLimit);
                            } else {
                                this.handleClose(ActionButtons.Cancel);
                            }
                        })
                    }}
                />
            </>
        );
    }

}

export default UserSettings;
